//
// Copyright (c) 2011-2020 by Curalate, Inc.
//
import moment from 'moment-timezone';

import Interval from '@models/Interval';
import { IUnisonDateRange } from '@models/UnisonTypes';

const DAY_FORMAT = 'YYYY-MM-DD';
const EARLIEST_DATE = new Date('2015-01-01');
const LATEST_DATE = moment().startOf('day').subtract(1, 'days'); // yesterday
const SHOWROOM_GALLERY_RELEASE_DATE: Date = new Date('2018-04-19');
const COMPLETE_UMS_START_DATE: Date = new Date('2018-01-01'); // not all stats
const ADOBE_REVENUE_START_DATE: Date = new Date('2020-05-01'); // limited adobe revenue
const DATE_TIME_DAILY_IDENTIFIER_FORMAT = 'M/D/YY';
const DATE_TIME_HOURLY_IDENTIFIER_FORMAT = 'hh:mm a';
export const MILLIS_PER_DAY = 1000 * 60 * 60 * 24;

// When we try to parse hourly identifiers as daily stats, the year returned is 2000
// This is how we identify if a stat is hourly or daily in the highcharts graph
const HOURLY_STAT_YEAR = 2000;

function maxDate(left: Date, right: Date): Date {
  return new Date(Math.max(left.getTime(), right.getTime()));
}

function getMinDate(clientCreationDate: Date): Date {
  // We subtract one day from the client creation date to account for timezone issues
  // Since this could be executed in a different time zone than the time zone of the created date
  // We can run into OBOEs
  clientCreationDate.setDate(clientCreationDate.getDate() - 1);
  return maxDate(clientCreationDate, EARLIEST_DATE);
}

function isDateValid(
  from: Date,
  clientCreation: Date,
  earliestAvailableDate: Date,
): boolean {
  const fromDate = from || EARLIEST_DATE;
  const clientCreationDate = getMinDate(clientCreation);
  const startDate = maxDate(fromDate, clientCreationDate);
  return startDate.getTime() <= earliestAvailableDate.getTime();
}

/**
 * Returns the overlap between the provided dateRange and the dateRange of available analytics for the client
 *
 * The available dates of client analytic data is restricted to [ max(2015-01-01, clientCreation) -> yesterday ]
 *
 * @param dateRange The date range that should be cropped to fit available analytic data
 * @param clientCreation If available, the known earliest availability of analytic data
 */
function fromDateRange(
  dateRange: IUnisonDateRange,
  clientCreation: Date | number,
): Interval {
  if (dateRange.alias === 'last24Hours') {
    // For a last 24 hour range, ignore the date passed and use utc moments
    // This is because the server interprets the dates in UTC
    // and checks for a startDate = endDate = today
    const start = moment.utc();
    const end = moment.utc();

    return Interval.fromMoments(start, end);
  } else {
    // Custom ranges require an offset of 0 whereas preset ranges require an offset of 1
    // This is because the date range picker selects the last 7 days including today
    // when we want the last 7 days excluding today
    const offsetDateRange =
      dateRange.alias !== 'custom'
        ? {
            startDate: dateRange.startDate
              ? moment(dateRange.startDate).subtract(1, 'days').toDate()
              : undefined,
            endDate: dateRange.endDate
              ? moment(dateRange.endDate).subtract(1, 'days').toDate()
              : undefined,
            alias: 'custom',
          }
        : dateRange;

    const earliestClientRecord = moment.max(
      moment(clientCreation),
      moment(EARLIEST_DATE),
    );

    // Clamp left
    const startDate = offsetDateRange.startDate
      ? moment.max(moment(offsetDateRange.startDate), earliestClientRecord)
      : earliestClientRecord;

    // Clamp Right
    const endDate = offsetDateRange.endDate
      ? moment.min(moment(offsetDateRange.endDate), LATEST_DATE)
      : LATEST_DATE;

    if (endDate.isBefore(startDate)) {
      return Interval.fromMoments(startDate, startDate);
    }

    return Interval.fromMoments(startDate, endDate);
  }
}

function dateTimeIdentifierIsHourly(dateTimeIdentifier: string): boolean {
  return (
    moment(dateTimeIdentifier, DATE_TIME_DAILY_IDENTIFIER_FORMAT).year() ===
    HOURLY_STAT_YEAR
  );
}

function aliasToDateRange(dateObj: IUnisonDateRange): {
  startDate: string;
  endDate: string;
} {
  let momentDateRange;
  switch (dateObj.alias) {
    case 'last24Hours':
      momentDateRange = {
        startDate: moment(),
        endDate: moment(),
      };
      break;
    case 'last7Days':
      momentDateRange = {
        startDate: moment().subtract(7, 'days'),
        endDate: moment().subtract(1, 'days'),
      };
      break;
    case 'last30Days':
      momentDateRange = {
        startDate: moment().subtract(30, 'days'),
        endDate: moment().subtract(1, 'days'),
      };
      break;
    case 'last3Months':
      momentDateRange = {
        startDate: moment().subtract(3, 'months').subtract(1, 'days'),
        endDate: moment().subtract(1, 'days'),
      };
      break;
    case 'custom':
      momentDateRange = {
        startDate: moment(dateObj.startDate),
        endDate: moment(dateObj.endDate),
      };
      break;
    default:
      throw Error(`Unexpected date range alias: ${dateObj.alias}`);
  }
  return {
    startDate: momentDateRange.startDate.format(DAY_FORMAT),
    endDate: momentDateRange.endDate.format(DAY_FORMAT),
  };
}

function getComparisonDateRange(dateObj: IUnisonDateRange): IUnisonDateRange {
  // Calculate the number of days between the start and end date of the dateRange
  // We divide by the number of milliseconds in a day (1000*60*60*24) to get the number of days
  const span =
    (dateObj.endDate.getTime() - dateObj.startDate.getTime()) / MILLIS_PER_DAY;
  return {
    alias: 'custom',
    startDate: moment(dateObj.startDate)
      .subtract(span + 1, 'days')
      .toDate(),
    endDate: moment(dateObj.endDate)
      .subtract(span + 1, 'days')
      .toDate(),
  };
}

function readableInterval(dateObj: IUnisonDateRange): string {
  return `${
    dateObj.startDate.getMonth() + 1
  }/${dateObj.startDate.getDate()}/${dateObj.startDate.getFullYear()} - ${
    dateObj.endDate.getMonth() + 1
  }/${dateObj.endDate.getDate()}/${dateObj.endDate.getFullYear()}`;
}

export default {
  EARLIEST_DATE,
  COMPLETE_UMS_START_DATE,
  ADOBE_REVENUE_START_DATE,
  DATE_TIME_DAILY_IDENTIFIER_FORMAT,
  DATE_TIME_HOURLY_IDENTIFIER_FORMAT,

  pastDays: (numDays: number, offset = 0): Interval => {
    const end = moment().subtract(offset, 'days');
    const start = moment().subtract(numDays + offset, 'days');
    return Interval.fromMoments(start, end);
  },

  getMinDate: (clientCreation: number = LATEST_DATE.valueOf()): Date => {
    return getMinDate(new Date(clientCreation));
  },

  fromDateRange: (
    dateRange: IUnisonDateRange,
    clientCreation: Date | number = LATEST_DATE.valueOf(),
  ) => fromDateRange(dateRange, clientCreation),

  aliasToDateRange: (alias: IUnisonDateRange) => aliasToDateRange(alias),

  isBeforeShowroomGallery: (fromDate: Date, clientCreation: Date) => {
    return isDateValid(fromDate, clientCreation, SHOWROOM_GALLERY_RELEASE_DATE);
  },

  isDateBeforeUMSComplete: (fromDate: Date, clientCreation: Date) => {
    return isDateValid(fromDate, clientCreation, COMPLETE_UMS_START_DATE);
  },

  isDateBeforeAdobeRevenue: (fromDate: Date, clientCreation: Date) => {
    return isDateValid(fromDate, clientCreation, ADOBE_REVENUE_START_DATE);
  },

  isToday: (intervalString: Interval) => {
    const dateRange = fromDateRange(
      {
        // We ignore the start/end dates of the last24Hours alias in fromDateRange
        alias: 'last24Hours',
      } as IUnisonDateRange,
      0,
    );

    return dateRange === intervalString;
  },

  isIntervalBeforeUMSComplete: (intervalString: Interval) => {
    const startDate = intervalString.split('/')[0];
    return moment(startDate, DAY_FORMAT).isBefore(
      moment(COMPLETE_UMS_START_DATE),
    );
  },

  intervalToUMSCompleteInterval: (intervalString: Interval) => {
    const startDate = moment(COMPLETE_UMS_START_DATE);
    const endDate = moment(intervalString.split('/')[1]);
    return Interval.fromMoments(startDate, endDate);
  },

  dateTimeIdentifierIsBeforeUMSComplete: (dateTimeIdentifier: string) => {
    const isToday = dateTimeIdentifierIsHourly(dateTimeIdentifier);

    const formattedMoment = moment(
      dateTimeIdentifier,
      DATE_TIME_DAILY_IDENTIFIER_FORMAT,
    );
    const completeUMSStartDate = moment(COMPLETE_UMS_START_DATE);
    return !isToday && formattedMoment.isBefore(completeUMSStartDate);
  },

  dateTimeIdentifierIsHourly: (dateTimeIdentifier: string) =>
    dateTimeIdentifierIsHourly(dateTimeIdentifier),

  timePostedIsBeforeUMSComplete: (timePosted: number) => {
    const formattedMoment = moment(timePosted);
    const completeUMSStartDate = moment(COMPLETE_UMS_START_DATE);
    return formattedMoment.isBefore(completeUMSStartDate);
  },

  readableInterval: (date: IUnisonDateRange) => readableInterval(date),

  getComparisonDateRange: (date: IUnisonDateRange) =>
    getComparisonDateRange(date),
};
